import { useState } from 'react';
import Reveal from '@/components/feature/Reveal';
import { guideSteps, faqs } from '@/mocks/home';

function FaqItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(index === 0);

  return (
    <div className="border-b border-background-200/70 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full cursor-pointer items-center justify-between gap-4 py-4 text-left"
      >
        <span className="font-heading text-sm font-bold text-foreground-950 md:text-base">{q}</span>
        <i
          className={`shrink-0 text-lg text-secondary-600 transition-transform duration-300 ${
            open ? 'ri-subtract-line' : 'ri-add-line'
          }`}
        />
      </button>
      <div
        className={`overflow-hidden text-sm leading-relaxed text-foreground-600 transition-all duration-300 ${
          open ? 'max-h-40 pb-4 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {a}
      </div>
    </div>
  );
}

export default function VisitGuide() {
  return (
    <section id="guide" className="scroll-mt-28 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <Reveal>
          <header className="max-w-2xl">
            <p className="text-xs font-medium uppercase tracking-[0.3em] text-secondary-700">
              HOW TO VISIT
            </p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              四步走，不再找不到官网
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              大多数「打不开」其实只是线路切换没跟上。按下面的顺序操作，基本一次就能进。
            </p>
          </header>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {guideSteps.map((step, index) => (
            <Reveal key={step.step} delay={index * 80} className="h-full">
              <div className="flex h-full flex-col rounded-lg border border-background-200/80 bg-background-50 p-5">
                <span className="font-heading text-3xl font-bold text-primary-200">{step.step}</span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="rounded-lg bg-primary-600 p-6 text-background-50">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-50/15">
                <i className="ri-question-answer-line text-xl" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold">还有疑问？</h3>
              <p className="mt-2 text-sm leading-relaxed text-primary-100">
                下面是大家问得最多的几个问题。如果仍然解决不了，可以到讨论区留言，值班编辑会逐个回复。
              </p>
              <a
                href="#discussion"
                className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-background-100"
              >
                去讨论区提问
                <i className="ri-arrow-right-line text-base" />
              </a>
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-8">
            <div className="rounded-lg border border-background-200/80 bg-background-50 px-6">
              {faqs.map((faq, index) => (
                <FaqItem key={faq.q} q={faq.q} a={faq.a} index={index} />
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}